import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Service } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { Percent, Minus, Plus } from "lucide-react";

interface ServicePriceDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedServices: Service[];
  onConfirm: (servicesWithPrices: ServiceWithPrice[]) => void;
}

interface ServiceWithPrice {
  service: Service;
  modifiedPrice: number;
  originalPrice: number;
}

export const ServicePriceDialog = ({ 
  isOpen, 
  setIsOpen, 
  selectedServices, 
  onConfirm 
}: ServicePriceDialogProps) => {
  const [servicesWithPrices, setServicesWithPrices] = useState<ServiceWithPrice[]>([]);
  const { toast } = useToast();

  // Initialize services with original prices when dialog opens
  useEffect(() => {
    if (isOpen && selectedServices.length > 0) {
      const initializedServices = selectedServices.map(service => ({
        service,
        modifiedPrice: service.price,
        originalPrice: service.price
      }));
      setServicesWithPrices(initializedServices);
    }
  }, [isOpen, selectedServices]);

  const handlePriceChange = (serviceId: string, newPrice: string) => {
    const price = parseFloat(newPrice) || 0;
    setServicesWithPrices(prev => 
      prev.map(item => 
        item.service.id === serviceId 
          ? { ...item, modifiedPrice: price }
          : item
      )
    );
  };

  const handleDiscountPercentage = (serviceId: string, percentage: number) => {
    setServicesWithPrices(prev => 
      prev.map(item => {
        if (item.service.id === serviceId) {
          const discountAmount = (item.originalPrice * percentage) / 100;
          const newPrice = item.originalPrice - discountAmount;
          return { ...item, modifiedPrice: Math.max(0, newPrice) };
        }
        return item;
      })
    );
  };

  const handleConfirm = () => {
    // Validate that all prices are valid
    const invalidServices = servicesWithPrices.filter(item => 
      item.modifiedPrice < 0 || isNaN(item.modifiedPrice)
    );
    
    if (invalidServices.length > 0) {
      toast({
        title: "Invalid prices",
        description: "Please enter valid prices for all services",
        variant: "destructive"
      });
      return;
    }

    onConfirm(servicesWithPrices);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const getDiscountPercentage = (originalPrice: number, modifiedPrice: number) => {
    if (originalPrice === 0) return 0;
    return Math.round(((originalPrice - modifiedPrice) / originalPrice) * 100);
  };

  const getPriceChangeType = (originalPrice: number, modifiedPrice: number) => {
    if (modifiedPrice > originalPrice) return "increase";
    if (modifiedPrice < originalPrice) return "discount";
    return "same";
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Modify Service Prices</DialogTitle>
          <DialogDescription>
            Adjust the prices for the selected services. Original prices will remain unchanged in the database.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {servicesWithPrices.map((item) => {
            const discountPercentage = getDiscountPercentage(item.originalPrice, item.modifiedPrice);
            const changeType = getPriceChangeType(item.originalPrice, item.modifiedPrice);
            
            return (
              <div key={item.service.id} className="border rounded-lg p-4 space-y-3">
                <div>
                  <h4 className="font-medium">{item.service.name}</h4>
                  <p className="text-sm text-muted-foreground">{item.service.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`original-${item.service.id}`}>Original Price</Label>
                    <Input
                      id={`original-${item.service.id}`}
                      value={`₹${item.originalPrice.toFixed(2)}`}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor={`modified-${item.service.id}`}>Modified Price</Label>
                    <Input
                      id={`modified-${item.service.id}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.modifiedPrice}
                      onChange={(e) => handlePriceChange(item.service.id, e.target.value)}
                      className={`[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                        changeType === "discount" ? "border-green-500" : 
                        changeType === "increase" ? "border-blue-500" : ""
                      }`}
                    />
                  </div>
                </div>
                
                {/* Quick discount buttons */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Quick discounts:</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDiscountPercentage(item.service.id, 5)}
                    className="h-8 px-2"
                  >
                    <Percent className="h-3 w-3 mr-1" />
                    5%
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDiscountPercentage(item.service.id, 10)}
                    className="h-8 px-2"
                  >
                    <Percent className="h-3 w-3 mr-1" />
                    10%
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDiscountPercentage(item.service.id, 15)}
                    className="h-8 px-2"
                  >
                    <Percent className="h-3 w-3 mr-1" />
                    15%
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDiscountPercentage(item.service.id, 20)}
                    className="h-8 px-2"
                  >
                    <Percent className="h-3 w-3 mr-1" />
                    20%
                  </Button>
                </div>
                
                {/* Price change indicator */}
                {changeType !== "same" && (
                  <div className={`text-sm p-2 rounded ${
                    changeType === "discount" 
                      ? "bg-green-50 text-green-700 border border-green-200" 
                      : "bg-blue-50 text-blue-700 border border-blue-200"
                  }`}>
                    {changeType === "discount" ? (
                      <>
                        <Minus className="h-3 w-3 inline mr-1" />
                        {discountPercentage}% discount (₹{(item.originalPrice - item.modifiedPrice).toFixed(2)} off)
                      </>
                    ) : (
                      <>
                        <Plus className="h-3 w-3 inline mr-1" />
                        ₹{(item.modifiedPrice - item.originalPrice).toFixed(2)} increase
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Confirm & Add Services
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
